import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import styled from "styled-components";
import { FormError, SubmitButton } from "./library/Form";
import {
  Modal,
  ModalBackground,
  ModalContent,
  ModalClose,
  ModalHeading,
} from "./library/Modal";
import { Form, Formik } from "formik";
import RecruiterUser from "../apiResources/RecruiterUser";
import EmployerUser from "../apiResources/EmployerUser";
import ReassignOwnership from "../apiResources/ReassignOwnership";
import Alert from "./library/Alert";
import Button from "./library/Button";
import ButtonGroup from "./library/ButtonGroup";
import RadioFieldGroup from "./library/Form/RadioFieldGroup";
import RadioField from "./library/Form/RadioField";
import { useTeamMembers } from "../api/company";
import SettingHelpText from "./settings/SettingHelperText";
import Loader from "./library/Loader";
import StatusTag from "./StatusTag";
import { useCurrentUser } from "../api/user";

type RemoveTeamMemberModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  user: RecruiterUser | EmployerUser;
  onSuccess: () => void;
};

const RemoveTeamMemberModal = (props: RemoveTeamMemberModalProps) => {
  const { isOpen, onDismiss, user, onSuccess } = props;
  const [formError, setFormError] = useState("");

  const { data: currentUser, isLoading: loadingUser } = useCurrentUser();
  const { data, isLoading: loadingMembers } = useTeamMembers({ per: 999 });
  const otherMembers = data?.members;

  if (!isOpen) {
    return null;
  }

  if (loadingUser || loadingMembers) {
    return <Loader />;
  }

  const removeTeamMember = async (values) => {
    try {
      let reassignmentSuccess = null;

      if (values.reassignTo) {
        reassignmentSuccess = await new ReassignOwnership({
          newOwnerId: values.reassignTo,
          oldOwnerId: user.id,
        }).save();
      }

      if (!values.reassignTo || reassignmentSuccess) {
        await user.destroy().then(() => {
          onSuccess();
        });
      }
    } catch (err) {
      setFormError("😬 Uh oh, something went wrong there!");
      Sentry.captureException(err);
    }
  };

  // Only shows users that are not the one being deleted and have verified their account.
  const reassignOptions = otherMembers.filter(
    (m) => m.id !== user.id && !m.invitationPending
  );

  return (
    <Modal id="remove-member-modal" isOpen={isOpen} onDismiss={onDismiss}>
      <ModalBackground onClick={onDismiss} />
      <ModalContent>
        <ModalHeading>Remove Team Member</ModalHeading>

        <Alert status="error" variant="ghost">
          This will delete the account of{" "}
          <strong>
            {user.fullName} ({user.email})
          </strong>{" "}
          and they will no longer be able to access your company account on
          Hiring Hub.
        </Alert>

        {formError && <FormError>{formError}</FormError>}

        <Formik
          initialValues={{
            reassignTo:
              reassignOptions.length === 1 ? reassignOptions[0].id : "",
          }}
          onSubmit={removeTeamMember}
          validationSchema={Yup.object({
            reassignTo: Yup.string().required(
              "Please select a user to reassign to"
            ),
          })}
        >
          <SpacedForm>
            <RadioFieldGroup
              vertical
              name="reassignTo"
              label="Who should we reassign jobs, notifications, engagements, and company ownership (if applicable) to?"
            >
              {reassignOptions.map((member) => (
                <RadioField key={member.id} name="reassignTo" value={member.id}>
                  <UserLabel>
                    {member.fullName} ({member.email})
                  </UserLabel>
                  {member.id === currentUser.id && (
                    <StatusTag status="info">Your account</StatusTag>
                  )}
                </RadioField>
              ))}
            </RadioFieldGroup>

            <SettingHelpText>
              We need you to let us know the best person to take over any
              existing data that might be associated with{" "}
              <strong>{user.fullName}</strong> so we can make sure someone is
              able to manage it going forward.
            </SettingHelpText>

            <ButtonGroup>
              <SubmitButton submittingText="Removing...">Remove</SubmitButton>
              <Button tertiary onClick={onDismiss}>
                Cancel
              </Button>
            </ButtonGroup>
          </SpacedForm>
        </Formik>

        <ModalClose onClick={onDismiss} aria-label="Close candidate modal" />
      </ModalContent>
    </Modal>
  );
};

const SpacedForm = styled(Form)`
  margin-top: 2rem;
`;

const UserLabel = styled.div`
  display: inline-block;
  margin-right: 0.5rem;
`;

export default RemoveTeamMemberModal;
